import React, { useEffect, useState } from "react";
import { Sparkles, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { api, aiJob } from "@/lib/api";
import { PageHeader, SectionCard, StatusPill } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import { ClinicalBanner, AIOutputDisclaimer } from "@/components/Disclaimer";
import AiAnswerCard from "@/components/AiAnswerCard";
import ShowReasoning from "@/components/ShowReasoning";
import { exportToPDF } from "@/lib/pdf";

export default function PriorAuth() {
  const [list, setList] = useState([]);
  const [patients, setPatients] = useState([]);
  const [form, setForm] = useState({ patient_id: "", payer_name: "", payer_id: "", procedure: "", cpt: "", clinical_justification: "" });
  const [busy, setBusy] = useState(false);
  const [letter, setLetter] = useState(null);

  const load = () => api.get("/prior-auths").then((r)=>setList(r.data));

  useEffect(() => {
    load();
    api.get("/patients").then((r)=>{ setPatients(r.data); setForm(f=>({...f, patient_id: r.data[0]?.id || ""})); });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/prior-auths", { ...form, payer_id: form.payer_id || form.payer_name.toLowerCase() });
      toast.success("Prior auth submitted");
      setForm({ ...form, procedure: "", cpt: "", clinical_justification: "" });
      load();
    } finally { setBusy(false); }
  };

  const draftLetter = async (id) => {
    setBusy(true);
    try {
      const data = await aiJob(`/prior-auths/${id}/generate-letter`, {});
      setLetter({ id, text: data.final, score: data.score, reasoning: data.reasoning, verification_footer: data.verification_footer });
      toast.success("Letter drafted by Topology AI");
      load();
    } catch (e) {
      toast.error("Letter generation failed");
    } finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    await api.patch(`/prior-auths/${id}`, { status });
    toast.success(`Marked ${status}`);
    load();
  };

  const columns = [
    { key: "patient_name", label: "Patient", render: (r) => patients.find(p=>p.id===r.patient_id)?.name || "—" },
    { key: "payer_name", label: "Payer" },
    { key: "procedure", label: "Procedure", render: (r)=>`${r.procedure} (${r.cpt})` },
    { key: "submitted_date", label: "Submitted", render: (r)=>new Date(r.submitted_date).toLocaleDateString() },
    { key: "decision_date", label: "Decision", render: (r)=>r.decision_date?new Date(r.decision_date).toLocaleDateString():"—" },
    { key: "status", label: "Status", render: (r)=><StatusPill status={r.status} /> },
    { key: "actions", label: "Actions", sortable: false, render: (r) => (
      <div className="flex gap-1.5">
        <button onClick={()=>draftLetter(r.id)} className="text-xs px-2 py-1 rounded border border-brand hover:bg-brand-bg" data-testid={`draft-letter-${r.id}`}>Letter</button>
        {r.status === "pending" && (
          <>
            <button onClick={()=>setStatus(r.id, "approved")} className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700">Approve</button>
            <button onClick={()=>setStatus(r.id, "denied")} className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">Deny</button>
          </>
        )}
      </div>
    )},
  ];

  return (
    <>
      <PageHeader eyebrow="Module 2" title="Prior Authorization AI"
        subtitle="Draft clinical justification letters with the topology agent and track every PA from submission to decision." testid="pa-page" />
      <ClinicalBanner />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <SectionCard title="New Prior Authorization" testid="pa-form">
          <form onSubmit={submit} className="space-y-3">
            <select value={form.patient_id} onChange={(e)=>setForm({...form, patient_id: e.target.value})} required
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="pa-patient">
              {patients.map((p)=><option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <input placeholder="Payer name (e.g. BlueCross)" value={form.payer_name} onChange={(e)=>setForm({...form, payer_name:e.target.value})} required
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="pa-payer" />
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Procedure" value={form.procedure} onChange={(e)=>setForm({...form, procedure:e.target.value})} required
                className="px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="pa-procedure" />
              <input placeholder="CPT" value={form.cpt} onChange={(e)=>setForm({...form, cpt:e.target.value})} required
                className="px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="pa-cpt" />
            </div>
            <textarea placeholder="Clinical justification…" value={form.clinical_justification} onChange={(e)=>setForm({...form, clinical_justification:e.target.value})}
              rows={4} className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="pa-justification" />
            <button type="submit" disabled={busy}
              className="w-full bg-brand-primary text-white py-2 rounded-md text-sm font-medium hover:bg-[#35096B] disabled:opacity-50" data-testid="pa-submit">
              Submit Prior Auth
            </button>
          </form>
        </SectionCard>

        <div className="lg:col-span-2">
          <SectionCard title="Drafted Letter" subtitle={letter ? `Score ${letter.score}/100` : "Use 'Letter' on any row to generate."}
            actions={letter && (
              <button onClick={()=>exportToPDF("Prior Authorization Letter", letter.text)} className="text-xs px-3 py-1.5 rounded-md border border-brand hover:bg-brand-bg inline-flex items-center gap-1.5">
                <Download className="w-3.5 h-3.5" /> PDF
              </button>
            )}
            testid="pa-letter">
            {busy && <div className="flex items-center gap-2 text-sm text-brand-muted"><Loader2 className="w-4 h-4 animate-spin" /> Topology agent drafting…</div>}
            {!busy && letter && (
              <>
                <AIOutputDisclaimer />
                <pre className="text-sm whitespace-pre-wrap leading-relaxed text-brand-text max-h-96 overflow-y-auto" data-testid="pa-letter-text">{letter.text}</pre>
                {letter.verification_footer && (
                  <div className="mt-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-300 text-xs text-amber-900 italic" data-testid="pa-verification-footer">
                    {letter.verification_footer}
                  </div>
                )}
                <ShowReasoning reasoning={letter.reasoning} score={letter.score} testid="pa-reasoning" />
                <AiAnswerCard
                  platformModule="prior-auth"
                  originalQuery={`${form.procedure || ""} PA for payer ${form.payer_name || ""}`}
                  originalAiResponse={letter.text}
                  retrievedEntryIds={letter.retrieved_entry_ids || []}
                  suggestedDomain="prior_auth"
                  testid="pa-answer-card"
                />
              </>
            )}
            {!busy && !letter && <div className="text-sm text-brand-muted">No letter drafted yet.</div>}
          </SectionCard>
        </div>
      </div>

      <SectionCard title="Prior Auth Tracker" testid="pa-tracker">
        <DataTable data={list} columns={columns} searchKeys={["payer_name","procedure","cpt","status"]} testid="pa-table" />
      </SectionCard>
    </>
  );
}
